import React, { useState, useEffect } from "react";

import { Plus, Edit, Delete } from "icons";

import providerManagerAssociationsApi from "apis/providerManagerAssociation";
import { Alert, Button, Table } from "clearsense-ui/components";
import PermissibleWrapper from "components/Common/PermissibleWrapper";
import formatPermissions from "utils/formatPermissions";

import AddAssociation from "./AddAssociation";

import { ASSOCIATIONS_COLUMNS } from "../constants";

const AssociationTab = ({
  sourceEntityItemID,
  currentEntityID,
  activeTab,
  isViewMode,
  userPermissions,
  allAssociations,
  entities,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [associations, setAssociations] = useState([]);
  const [isAddingAssociation, setIsAddingAssociation] = useState(false);
  const [associationToDelete, setAssociationToDelete] = useState();
  const [associationToEdit, setAssociationToEdit] = useState();
  const [isShowDeleteModal, setIsShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const associationTemplate = allAssociations?.find(
    item => item.to === activeTab.template_id
  )?.fields;
  const sourceEntityItemKey = entities?.find(
    entity => entity.id === currentEntityID
  )?.entity_key_name;
  const targetEntityItemKey = entities?.find(
    entity => entity.id === activeTab.id
  )?.entity_key_name;

  const fetchEntityAssociation = async () => {
    setIsLoading(true);
    try {
      const { data } = await providerManagerAssociationsApi.fetch_associations(
        sourceEntityItemID,
        activeTab.id
      );
      setAssociations([
        ...data.forward_associations.map(item => ({
          ...item,
          direction: "Forward",
        })),
        ...data.backward_associations.map(item => ({
          ...item,
          direction: "Backward",
        })),
      ]);
    } catch (err) {
      logger.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEntityAssociation();
  }, []);

  const handleAddAssociation = async values => {
    const valuesCopy = { ...values };
    delete valuesCopy.target_key;
    delete valuesCopy.start_date;
    delete valuesCopy.end_date;
    setIsLoading(true);
    try {
      associationToEdit
        ? await providerManagerAssociationsApi.update(associationToEdit, {
            association_type: values.association_type,
            effective_start_date: values.start_date,
            effective_end_date: values.end_date,
            template_fields_data: valuesCopy,
          })
        : await providerManagerAssociationsApi.create({
            source_entity_id: currentEntityID,
            source_entity_item_id: sourceEntityItemID,
            target_entity_id: activeTab.id,
            target_entity_item_id: values.target_key.value,
            association_type: values.association_type,
            effective_start_date: values.start_date,
            effective_end_date: values.end_date,
            template_fields_data: valuesCopy,
          });
    } catch (err) {
      logger.log(err);
    } finally {
      setIsAddingAssociation(false);
      fetchEntityAssociation();
      setIsLoading(false);
      setAssociationToEdit(null);
    }
  };

  const handleDeleteAssociation = async () => {
    setIsDeleting(true);
    try {
      await providerManagerAssociationsApi.delete(associationToDelete);
    } catch (err) {
      logger.log(err);
    } finally {
      setIsAddingAssociation(false);
      setIsDeleting(false);
      setIsShowDeleteModal(false);
      setAssociationToEdit(null);
      fetchEntityAssociation();
    }
  };

  const generateAssociationTableColumns = () => [
    ...ASSOCIATIONS_COLUMNS,
    {
      dataIndex: "source_entity_item_id",
      key: "source_entity_item_id",
      title: sourceEntityItemKey,
    },
    {
      dataIndex: "target_entity_item_id",
      key: "target_entity_item_id",
      title: targetEntityItemKey,
    },
    ...(associationTemplate?.map(field => ({
      dataIndex: ["template_fields_data", field?.name],
      key: field?.name,
      title: field?.name,
    })) || []),
    ...((formatPermissions(userPermissions)
      ?.map(permission =>
        formatPermissions(["update", "destroy"])?.includes(permission)
      )
      ?.includes(true) ||
      userPermissions?.includes("all") ||
      userPermissions === "all") &&
    !isViewMode
      ? [
          {
            key: "Actions",
            width: 80,
            align: "center",
            fixed: "right",
            render: (_, record) => (
              <div className="flex space-x-3 justify-center">
                <PermissibleWrapper
                  currentPermissions={userPermissions}
                  acceptablePermissions="update"
                >
                  <Edit
                    size={20}
                    className="flex-shrink-0 cursor-pointer hover:text-primary-shade"
                    onClick={() => {
                      setAssociationToEdit(record.id);
                      setIsAddingAssociation(true);
                    }}
                  />
                </PermissibleWrapper>
                <PermissibleWrapper
                  currentPermissions={userPermissions}
                  acceptablePermissions="destroy"
                >
                  <Delete
                    size={20}
                    className="flex-shrink-0 cursor-pointer hover:text-red-500"
                    onClick={() => {
                      setAssociationToDelete(record.id);
                      setIsShowDeleteModal(true);
                    }}
                  />
                </PermissibleWrapper>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <Table
        rowData={associations}
        isLoading={isLoading}
        columnData={generateAssociationTableColumns()}
      />
      <div>
        {!isAddingAssociation ? (
          <PermissibleWrapper
            currentPermissions={userPermissions}
            acceptablePermissions="create"
          >
            {!isViewMode && (
              <Button
                label="Add Association"
                icon={Plus}
                onClick={() => setIsAddingAssociation(true)}
              />
            )}
          </PermissibleWrapper>
        ) : (
          <AddAssociation
            setIsAddingAssociation={setIsAddingAssociation}
            handleAddAssociation={handleAddAssociation}
            activeTab={activeTab}
            associationToEdit={associationToEdit}
            setAssociationToEdit={setAssociationToEdit}
            associationTemplate={associationTemplate}
          />
        )}
      </div>
      <Alert
        title="Delete Association"
        message="Are you sure you want to delete?"
        isOpen={isShowDeleteModal}
        onClose={() => {
          setIsShowDeleteModal(false);
          setAssociationToDelete(null);
        }}
        onSubmit={handleDeleteAssociation}
        isSubmitting={isDeleting}
      />
    </>
  );
};

export default AssociationTab;
